import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import {
  Button as AriaButton,
  OverlayArrow as AriaOverlayArrow,
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
} from 'react-aria-components';

import { cx } from '@/lib/utils/cx';

import type { ReactNode } from 'react';
import type {
  ButtonProps as AriaButtonProps,
  TooltipProps as AriaTooltipProps,
  TooltipTriggerComponentProps as AriaTooltipTriggerComponentProps,
} from 'react-aria-components';

interface TooltipProps
  extends AriaTooltipTriggerComponentProps, Omit<AriaTooltipProps, 'children'> {
  /**
   * The title of the tooltip.
   */
  readonly title: ReactNode;
  /**
   * The description of the tooltip.
   */
  readonly description?: ReactNode;
  /**
   * Whether to show the arrow on the tooltip.
   *
   * @default false
   */
  readonly arrow?: boolean;
  /**
   * Delay in milliseconds before the tooltip is shown.
   *
   * @default 300
   */
  readonly delay?: number;
  /**
   * Renders the overlay as a positioning wrapper only: no panel surface, ring,
   * shadow, padding, or title typography, and `description` and `arrow` are
   * not rendered — the content owns the whole surface, so panel extras would
   * paint styled fragments around it. Content that carries its own card
   * chrome (`shared/hover-card`) would otherwise nest a second bordered
   * surface inside this one and read as a double outline.
   *
   * @default false
   */
  readonly unstyled?: boolean;
}

/** Reports whether the owning component's effects are connected. React
 *  subscribes in an effect and unsubscribes on cleanup, so the subscription
 *  lifecycle is the disconnect/reconnect signal a hidden `<Activity>` emits
 *  and nothing else exposes. The snapshot flips inside (un)subscribe and the
 *  listener is notified at once, so the consumer re-renders on both edges; the
 *  mount is reported connected up front so the first render needs no
 *  correction pass. `onDisconnect` runs on every disconnect, unmount included. */
function createConnectionStore(onDisconnect: () => void) {
  let isConnected = true;
  return {
    subscribe: (onChange: () => void) => {
      if (!isConnected) {
        isConnected = true;
        onChange();
      }
      return () => {
        isConnected = false;
        onDisconnect();
        onChange();
      };
    },
    getSnapshot: () => isConnected,
  };
}

export const Tooltip = ({
  title,
  description,
  children,
  arrow = false,
  delay = 300,
  unstyled = false,
  closeDelay = 0,
  trigger,
  isDisabled,
  isOpen,
  defaultOpen,
  offset = 6,
  crossOffset,
  placement = 'top',
  onOpenChange,
  ...tooltipProps
}: TooltipProps) => {
  // A hidden `<Activity>` (tab switch, closing a thread) keeps the trigger
  // mounted with its effects torn down, and React only sets `display: none` on
  // the subtree's TOPMOST host nodes — it never descends into them, so a
  // tooltip portalled to `<body>` from under any wrapper element is left
  // visible. One that is open when the hide lands has no way out (no hover end
  // from a `display: none` trigger), and one whose warm-up timer fires after
  // the hide mounts against a zero-size trigger at the viewport corner. Effect
  // connection tracks the hide exactly, so it is the signal: drop the overlay,
  // pin the state shut, and refuse opens until the subtree reconnects. While
  // hidden, effects stay off, so an exit animation could never report
  // completion — the overlay has to unmount, not close.
  const isControlled = isOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? false);
  const connection = useMemo(
    () =>
      createConnectionStore(() => {
        setUncontrolledOpen(false);
      }),
    [],
  );
  const isConnected = useSyncExternalStore(connection.subscribe, connection.getSnapshot);
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next && !connection.getSnapshot()) {
        return;
      }
      if (!isControlled) {
        setUncontrolledOpen(next);
      }
      onOpenChange?.(next);
    },
    [connection, isControlled, onOpenChange],
  );

  const isTopOrBottomLeft = ['top left', 'top end', 'bottom left', 'bottom end'].includes(
    placement,
  );
  const isTopOrBottomRight = ['top right', 'top start', 'bottom right', 'bottom start'].includes(
    placement,
  );
  // Set negative cross offset for left and right placement to visually balance the tooltip.
  let calculatedCrossOffset = 0;
  if (isTopOrBottomLeft) {
    calculatedCrossOffset = -12;
  } else if (isTopOrBottomRight) {
    calculatedCrossOffset = 12;
  }

  return (
    <AriaTooltipTrigger
      {...{ trigger, delay, closeDelay, isDisabled }}
      isOpen={isControlled ? isOpen : uncontrolledOpen}
      onOpenChange={handleOpenChange}
    >
      {children}

      {isConnected ? (
        <AriaTooltip
          {...tooltipProps}
          className={({ isEntering, isExiting }) =>
            cx(
              // The level belongs on THIS element — the positioned overlay React
              // Aria portals to `<body>` — not on the panel below, which is
              // statically positioned and so takes no `z-index` at all. Same level
              // as `base/popover` (`popover.tsx`), because a tooltip on a control
              // inside a popover is portalled out of that popover's subtree and
              // becomes its sibling: DOM order decides the painting order only
              // while the two share a stacking level.
              'z-[100000]',
              isEntering && 'animate-in ease-out',
              isExiting && 'animate-out ease-in',
            )
          }
          crossOffset={crossOffset ?? calculatedCrossOffset}
          offset={offset}
          placement={placement}
        >
          {({ isEntering, isExiting }) => (
            <div
              className={cx(
                'origin-(--trigger-anchor-point) will-change-transform',
                !unstyled && [
                  'flex max-w-xs flex-col items-start gap-1 rounded-lg bg-surface px-3 shadow-[0px_6px_20px_rgba(0,0,0,0.07),0px_2px_6px_rgba(0,0,0,0.04)] ring-1 ring-[var(--color-border-secondary)]/60',
                  description ? 'py-3' : 'py-2',
                ],

                isEntering &&
                  'in-placement-left:slide-in-from-right-0.5 in-placement-right:slide-in-from-left-0.5 in-placement-top:slide-in-from-bottom-0.5 in-placement-bottom:slide-in-from-top-0.5 animate-in ease-out zoom-in-95 fade-in',
                isExiting &&
                  'in-placement-left:slide-out-to-right-0.5 in-placement-right:slide-out-to-left-0.5 in-placement-top:slide-out-to-bottom-0.5 in-placement-bottom:slide-out-to-top-0.5 animate-out ease-in zoom-out-95 fade-out',
              )}
            >
              {unstyled ? (
                title
              ) : (
                <div className="text-xs font-semibold text-secondary">{title}</div>
              )}

              {!unstyled && description ? (
                <span className="text-xs font-medium text-quaternary">{description}</span>
              ) : null}

              {!unstyled && arrow ? (
                <AriaOverlayArrow>
                  <svg
                    className="size-2.5 fill-[var(--color-bg-primary)] in-placement-left:-rotate-90 in-placement-right:rotate-90 in-placement-top:rotate-0 in-placement-bottom:rotate-180"
                    viewBox="0 0 100 100"
                  >
                    <path d="M0,0 L35.858,35.858 Q50,50 64.142,35.858 L100,0 Z" />
                  </svg>
                </AriaOverlayArrow>
              ) : null}
            </div>
          )}
        </AriaTooltip>
      ) : null}
    </AriaTooltipTrigger>
  );
};

type TooltipTriggerProps = AriaButtonProps;

export const TooltipTrigger = ({ children, className, ...buttonProps }: TooltipTriggerProps) => {
  return (
    <AriaButton
      {...buttonProps}
      className={(values) =>
        cx(
          'h-max w-max outline-hidden',
          typeof className === 'function' ? className(values) : className,
        )
      }
    >
      {children}
    </AriaButton>
  );
};
